/**
 * Copyright © 2026 Adnuntius AS.
 */
import {DOCUMENT, Inject, Injectable, OnDestroy} from '@angular/core';
import {UIRouter} from '@uirouter/core';
import {TranslateService} from '@ngx-translate/core';

import {AUTH_EVENT} from '../../components/auth/auth-constants';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {SESSION_EVENT} from '../common/constants/misc-constants';
import {buildStateTitle} from '../common/directives/html/state-title';
import {getRegisteredUrl} from '../config/registered-urls';
import {WINDOW} from '../angular-platform-providers';
import {AdnInterpolateService} from '../common/filters/adn-interpolate';
import {AppEventBusService} from '../common/events/app-event-bus.service';

const DEFAULT_THEME = 'solarized.dark';
const VALID_THEMES = [DEFAULT_THEME];
const LOADING_CLASS = 'loading';
const BUSY_CLASS = 'appBusy';
const MIN_BUSY_MS = 300;

@Injectable()
export class DocumentShellService implements OnDestroy {
  private initialized = false;
  /** Explicit busy from sign-in / sign-out (not cleared by route transition end). */
  private manualBusy = false;
  private clearManualBusyTimer: ReturnType<typeof setTimeout> | null = null;
  /** Busy while a UI-Router transition is in flight. */
  private transitionBusy = false;
  private destroyListeners: Array<() => void> = [];

  constructor(
    private readonly uiRouter: UIRouter,
    private readonly localNetworkProfile: LocalNetworkProfile,
    private readonly localUserProfile: LocalUserProfile,
    private readonly translateService: TranslateService,
    private readonly interpolate: AdnInterpolateService,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly eventBus: AppEventBusService,
    @Inject(WINDOW) private readonly window: Window
  ) {
  }

  init(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    this.destroyListeners.push(
      this.uiRouter.transitionService.onStart({}, () => {
        this.transitionBusy = true;
        this.syncBusyDom();
      }) as () => void,
      this.uiRouter.transitionService.onSuccess({}, () => {
        this.transitionBusy = false;
        this.syncBusyDom();
        this.updateTitle();
      }) as () => void,
      this.uiRouter.transitionService.onError({}, () => {
        this.transitionBusy = false;
        this.syncBusyDom();
      }) as () => void
    );

    const updateTheme = (_event?: any, user?: any) => this.updateTheme(user);
    this.destroyListeners.push(
      this.eventBus.on(AUTH_EVENT.loginSuccess, updateTheme),
      this.eventBus.on(SESSION_EVENT.userProfileUpdate, updateTheme)
    );

    this.updateTheme();
    this.updateTitle();
  }

  ngOnDestroy(): void {
    if (this.clearManualBusyTimer) {
      clearTimeout(this.clearManualBusyTimer);
      this.clearManualBusyTimer = null;
    }
    this.destroyListeners.forEach((destroyListener) => destroyListener());
    this.destroyListeners = [];
    this.manualBusy = false;
    this.transitionBusy = false;
    this.syncBusyDom();
  }

  /**
   * Show the wait cursor on the whole page (sign-in, sign-out, etc.).
   * The root class survives route teardown; SCSS owns the cursor.
   */
  setBusy(busy: boolean): void {
    if (this.clearManualBusyTimer) {
      clearTimeout(this.clearManualBusyTimer);
      this.clearManualBusyTimer = null;
    }
    if (busy) {
      this.manualBusy = true;
      this.syncBusyDom();
      return;
    }

    if (this.manualBusy) {
      this.clearManualBusyTimer = setTimeout(() => {
        this.clearManualBusyTimer = null;
        this.manualBusy = false;
        this.syncBusyDom();
      }, MIN_BUSY_MS);
      return;
    }

    this.manualBusy = false;
    this.syncBusyDom();
  }

  private syncBusyDom(): void {
    const busy = this.manualBusy || this.transitionBusy;
    const body = this.document.body;
    const documentElement = this.document.documentElement;
    if (!body || !documentElement) {
      return;
    }
    if (busy) {
      documentElement.classList.add(BUSY_CLASS);
      body.classList.add(LOADING_CLASS);
    } else {
      documentElement.classList.remove(BUSY_CLASS);
      body.classList.remove(LOADING_CLASS);
    }
    // Chrome/WebKit only repaint the mouse cursor icon on the next pointer move, so a
    // cursor:wait set purely by a script-added class is invisible until the user nudges
    // the mouse. Reading offsetHeight forces a synchronous layout that flushes the pending
    // cursor style immediately, without waiting for real pointer movement.
    void documentElement.offsetHeight;
  }

  private updateTitle(): void {
    const currentState = (this.uiRouter.stateService as any).$current || this.uiRouter.globals.current;
    if (!currentState?.name) {
      return;
    }
    this.document.title = buildStateTitle({
      currentState,
      dataPropertyPath: 'data.title',
      suffix: 'Adnuntius',
      separator: ' - ',
      window: this.window,
      getNetworkTranslations: title => this.localNetworkProfile.getNetworkTranslations(title),
      translateInstant: key => this.translateService.instant(key),
      interpolate: template => this.interpolate.compile(template)
    });
  }

  private updateTheme(user?: any): void {
    const profile = user || this.localUserProfile.get();
    const registeredUrl = getRegisteredUrl(this.window, this.localNetworkProfile.get('networkId'));
    const registeredTheme = registeredUrl?.theme;
    const preferredTheme = profile?.profile?.adnPrefs?.theme || '';
    const profileTheme = VALID_THEMES.indexOf(preferredTheme) > -1 ? preferredTheme : DEFAULT_THEME;
    this.document.body.dataset.userTheme = registeredTheme || profileTheme;
  }
}
